import { useEffect } from "react";
import { useRouter } from "next/router";
import { assign, createMachine } from "xstate";
import { useMachine } from "@xstate/react";
import { ParsedUrlQuery } from "querystring";
import { track } from "@src/utils/segment";

interface Values {
  externalId: string;
  vendor: string;
  vendorToken: string;
  userId: string;
  callback: string;
  ic: string;
  vendorInquiryId: string;
}

interface InitEvent extends Values {
  type: "LOADED" | "ERROR" | "EXPLAINER_CLICKED";
}

function pick(obj: ParsedUrlQuery, required: string[], optional: string[]) {
  const ret: any = {};
  required.forEach(key => {
    const value = obj[key];
    if (typeof value === "string" && value !== "") {
      ret[key] = value;
    } else {
      throw new Error(`${value} is invalid`);
    }
  });
  optional.forEach(key => (ret[key] = obj[key]));
  return ret;
}

const initMachine = createMachine({
  id: "init",
  initial: "loading",
  states: {
    loading: {
      on: {
        LOADED: "explainer",
        ERROR: "error",
      },
      after: {
        5000: { target: "error" },
      },
    },
    error: {
      type: "final",
    },
    explainer: {
      on: {
        EXPLAINER_CLICKED: "scan_id",
      },
      entry: assign(({ event: evt }) => {
        if (!evt) {
          return {};
        }

        return {
          externalId: evt.externalId,
          vendor: evt.vendor,
          vendorToken: evt.vendorToken,
          userId: evt.userId,
          callback: evt.callback,
          ic: evt.ic,
          vendorInquiryId: evt.vendorInquiryId,
        };
      }),
    },
    scan_id: {
      type: "final",
    },
  },
});

export const useStateMachine = () => {
  const { query } = useRouter();
  const [state, send, actor] = useMachine(initMachine);
  const oldValue = actor.getSnapshot().value.toString();

  useEffect(() => {
    const newValue = state.value.toString();

    track("State Transition", {
      old: oldValue,
      new: newValue,
      ...state.context,
    });

    if (newValue === "scan_id") {
      track("State Transition", {
        transitionType: "TRANSITION_TYPE_CHIME_PRIVACY_NOTICE",
        ...state.context,
      });
    }
  }, [state, oldValue]);

  useEffect(() => {
    if (Object.keys(query).length === 0) return;

    try {
      let values;
      if (query.ic) {
        values = pick(query, ["vendorInquiryId", "callback", "ic"], ["externalId", "vendor", "userId", "callback"]);
      } else {
        values = pick(query, ["externalId", "vendor", "userId", "callback"], ["vendorToken", "ic", "vendorInquiryId"]);
      }

      send({ type: "LOADED", ...values });
    } catch {
      send({ type: "ERROR" });
    }
  }, [query, send]);

  return { state, send, actor };
};
